import { errorForStatus, NetworkError } from '@/api/errors';
import { readReason } from '@/api/client';
import { config } from '@/config';
import { getAccessToken } from '@/features/auth/token';

/**
 * Bestandsupload (PLAN-FASE3.md WP-07): `POST /companies/{companyId}/uploads`,
 * used by Huisstijl's logo (`SettingsBrandingPage`) and Documenten's file
 * (`DocumentsPage`). The result's `url` is then saved exactly like a plain
 * URL field always was (`logo_url` / `file_url`) — this call only replaces
 * where that URL comes from.
 *
 * A real `fetch` call rather than `apiPost` from `src/api/client.ts`: that
 * helper always sends `application/json`, but this endpoint takes
 * `multipart/form-data` — a shape `src/api/schema.ts`'s hand-written types
 * don't model for request bodies (see that file's note on this operation).
 * The 400/403/network handling mirrors `client.ts`'s `request()` so callers
 * get the same `ApiError` subclasses either way.
 */
export interface UploadResult {
  url: string;
  filename: string;
}

export async function uploadCompanyFile(companyId: string, file: File): Promise<UploadResult> {
  const formData = new FormData();
  formData.append('file', file);

  const headers: Record<string, string> = { Accept: 'application/json' };
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let response: Response;
  try {
    response = await fetch(`${config.apiUrl}/companies/${encodeURIComponent(companyId)}/uploads`, {
      method: 'POST',
      headers,
      body: formData,
    });
  } catch (cause) {
    throw new NetworkError(`Upload to /companies/${companyId}/uploads failed: ${String(cause)}`);
  }

  if (!response.ok) {
    throw errorForStatus(
      response.status,
      `POST /companies/${companyId}/uploads failed`,
      await readReason(response),
    );
  }

  return (await response.json()) as UploadResult;
}
